import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import JsonTreeViewer, { collectExpandablePaths } from './JsonTreeViewer';

const JsonTreeModal = ({ show, onHide, title, data }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedPaths, setExpandedPaths] = useState(() => new Set(['$']));

  useEffect(() => {
    if (show) {
      setSearchQuery('');
      setExpandedPaths(new Set(['$']));
    }
  }, [show, data]);

  const allPaths = useMemo(() => {
    if (data == null || typeof data !== 'object') return ['$'];
    return collectExpandablePaths(data);
  }, [data]);

  const matchCount = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q || data == null) return null;
    const text = typeof data === 'object' ? JSON.stringify(data) : String(data);
    if (!text.toLowerCase().includes(q)) return 0;
    try {
      const walk = (node) => {
        if (node == null || typeof node !== 'object') {
          return String(node).toLowerCase().includes(q) ? 1 : 0;
        }
        return Object.entries(node).reduce((sum, [k, v]) => {
          let n = k.toLowerCase().includes(q) ? 1 : 0;
          n += walk(v);
          return sum + n;
        }, 0);
      };
      return walk(data);
    } catch {
      return null;
    }
  }, [data, searchQuery]);

  const handleTogglePath = (path) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const handleExpandAll = () => {
    setExpandedPaths(new Set(allPaths));
  };

  const handleCollapseAll = () => {
    setExpandedPaths(new Set(['$']));
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{title || 'JSON detail'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
          <Form.Control
            type="search"
            size="sm"
            placeholder="Search keys or values…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="json-tree-modal__search"
          />
          <Button variant="outline-secondary" size="sm" onClick={handleExpandAll}>
            Expand all
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={handleCollapseAll}>
            Collapse all
          </Button>
          {searchQuery.trim() && matchCount != null && (
            <span className="text-muted small">
              {matchCount > 0 ? `${matchCount} match${matchCount === 1 ? '' : 'es'}` : 'No matches'}
            </span>
          )}
        </div>
        <div className="json-tree-modal__body">
          {data == null ? (
            <span className="text-muted">—</span>
          ) : (
            <JsonTreeViewer
              data={data}
              expandedPaths={expandedPaths}
              searchQuery={searchQuery}
              onTogglePath={handleTogglePath}
            />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default JsonTreeModal;
